import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, Phone, Mail, Check, XCircle, Clock, Repeat } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useBookingActions } from '../lib/useBookingActions';
import { formatPhoneForDisplay, stripPhoneForTel } from '../lib/phone';
import { formatBookingRef } from '../lib/bookingRef';

/**
 * BookingDetailPanel — the single shared surface used by all four Diary
 * views (Today, Day, By-barber, Week) when a booking card is clicked.
 *
 * Design:
 *   • Desktop (≥ 720px): 420 px right-side drawer, slides in from the right.
 *   • Mobile (< 720px): bottom sheet, 88 vh height, slides up from bottom.
 *   • Both render as a portal on <body> above every other stacking
 *     context. Closes on Escape, X, and backdrop click.
 *
 * Fields displayed are ONLY those actually present in the booking /
 * customer / service / barber / shop records — no invented fields.
 *
 * Actions call the exact same hook (`useBookingActions`) as the kebab
 * "..." menu, so the two surfaces cannot drift apart.
 */

// ------- helpers -----------------------------------------------------
const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};
const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
const fmtTimestamp = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const fmtMoney = (n) => `£${Number(n || 0).toFixed(0)}`;
const durationMin = (start, end) => Math.round((new Date(end) - new Date(start)) / 60000);

// Booking status → { label, className } for the header pill.
const STATUS_META = {
  confirmed: { label: 'Confirmed', cls: 'blue' },
  completed: { label: 'Completed', cls: 'green' },
  cancelled: { label: 'Cancelled', cls: 'red' },
  no_show:   { label: 'No show',   cls: 'amber' },
};

// bookings.channel enum → human label. Values today: 'web'. Anything
// else is displayed verbatim.
const CHANNEL_LABEL = {
  web: 'Online',
  walk_in: 'Walk-in',
  admin: 'Added by shop',
};

// Shop in_shop_payment enum → human label. NULL is treated as 'either'.
const IN_SHOP_PAYMENT_LABEL = {
  cash: 'Cash',
  card: 'Card',
  either: 'Cash or card',
};

const StatusPill = ({ status, refundStatus }) => {
  const meta = STATUS_META[status] || { label: status || 'Unknown', cls: 'gray' };
  // A cancel where the Stripe refund failed reads "Cancelled — refund
  // failed" so the shop cannot miss it. Chased manually until fixed.
  const label = (status === 'cancelled' && refundStatus === 'failed')
    ? 'Cancelled \u2014 refund failed'
    : meta.label;
  return <span className={`bdp-status bdp-status-${meta.cls}`} data-testid="bdp-status">{label}</span>;
};

// Row: "Label" on the left, "value" on the right.
const Row = ({ label, children, testid }) => (
  <div className="bdp-row" data-testid={testid}>
    <div className="bdp-row-label">{label}</div>
    <div className="bdp-row-val">{children}</div>
  </div>
);

// Section header (small caps).
const Section = ({ title, children }) => (
  <div className="bdp-section">
    <h4 className="bdp-section-title">{title}</h4>
    {children}
  </div>
);

// -------- data loader -----------------------------------------------
const useBookingDetail = (bookingId, refreshKey) => {
  const [state, setState] = React.useState({ loading: true, booking: null, history: null, inShopPayment: null });

  React.useEffect(() => {
    if (!bookingId) { setState({ loading: false, booking: null, history: null, inShopPayment: null }); return; }
    let cancelled = false;
    (async () => {
      setState((s) => ({ ...s, loading: true }));
      // Booking + customer (all fields) + service + barber + shop.
      // The refund_* columns are added by add-refund-tracking.sql; if
      // the migration hasn't been applied yet, the enriched query
      // fails and we fall back to the legacy column set so the panel
      // still renders.
      const SELECT_FULL = `
          id, starts_at, ends_at, status, channel, created_at, shop_id, booking_number,
          deposit_paid, deposit_amount, paid_in_full,
          payment_status, balance_paid_method, balance_paid_amount, balance_paid_at,
          stripe_payment_intent_id, refund_status, refund_error, refund_stripe_id,
          customer:customers(id, name, phone, email, marketing_consent, created_at),
          service:services(id, name, price, duration_min),
          barber:barbers(id, name),
          shop:shops(id, name)
        `;
      const SELECT_LEGACY = `
          id, starts_at, ends_at, status, channel, created_at, shop_id,
          deposit_paid, deposit_amount, paid_in_full,
          payment_status, balance_paid_method, balance_paid_amount, balance_paid_at,
          stripe_payment_intent_id,
          customer:customers(id, name, phone, email, marketing_consent, created_at),
          service:services(id, name, price, duration_min),
          barber:barbers(id, name),
          shop:shops(id, name)
        `;
      let b = null;
      const { data, error } = await supabase
        .from('bookings')
        .select(SELECT_FULL)
        .eq('id', bookingId)
        .maybeSingle();
      if (error && /(refund_|booking_number)/.test(error.message || '')) {
        const fallback = await supabase
          .from('bookings')
          .select(SELECT_LEGACY)
          .eq('id', bookingId)
          .maybeSingle();
        b = fallback.data;
      } else {
        b = data;
      }

      if (cancelled) return;
      if (!b) { setState({ loading: false, booking: null, history: null, inShopPayment: null }); return; }

      // Shop-level in_shop_payment. Column may not exist on legacy schemas
      // (the migration `add-in-shop-payment.sql` hasn't been run) — we
      // catch and default to NULL so the panel still renders.
      let inShop = null;
      try {
        const { data: shop } = await supabase
          .from('shops')
          .select('in_shop_payment')
          .eq('id', b.shop_id)
          .maybeSingle();
        inShop = shop?.in_shop_payment || null;
      } catch (_e) { /* column missing on legacy schema — silently null */ }

      // Client history for this customer — same aggregation as /clients:
      //   visits = count of completed bookings
      //   lastVisit = latest past booking (completed/confirmed/no_show)
      let visits = 0, lastVisit = null;
      if (b.customer?.id) {
        const { data: rows } = await supabase
          .from('bookings')
          .select('starts_at, status')
          .eq('customer_id', b.customer.id);
        const now = Date.now();
        (rows || []).forEach((r) => {
          const t = new Date(r.starts_at).getTime();
          if (t <= now && ['completed', 'confirmed', 'no_show'].includes(r.status)) {
            if (!lastVisit || t > new Date(lastVisit).getTime()) lastVisit = r.starts_at;
          }
          if (r.status === 'completed') visits += 1;
        });
      }
      setState({ loading: false, booking: b, history: { visits, lastVisit }, inShopPayment: inShop });
    })();
    return () => { cancelled = true; };
  }, [bookingId, refreshKey]);

  return state;
};

// -------- main component --------------------------------------------
export const BookingDetailPanel = ({ bookingId, onClose, onChanged }) => {
  const navigate = useNavigate();
  const [refreshKey, setRefreshKey] = React.useState(0);
  const { loading, booking, history, inShopPayment } = useBookingDetail(bookingId, refreshKey);

  // Local onChanged: re-fetch this booking AND propagate to Diary so
  // the calendar view refreshes too.
  const bump = React.useCallback(() => {
    setRefreshKey((k) => k + 1);
    onChanged?.();
  }, [onChanged]);

  const actions = useBookingActions(booking, bump);

  // ESC + backdrop close.
  React.useEffect(() => {
    if (!bookingId) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [bookingId, onClose]);

  if (!bookingId) return null;

  // Amount paid = stripe portion (deposit or full) + in-shop balance.
  const stripePaid = booking?.paid_in_full
    ? Number(booking?.service?.price || 0)
    : booking?.deposit_paid ? Number(booking?.deposit_amount || 0) : 0;
  const inShopPaid = Number(booking?.balance_paid_amount || 0);
  const amountPaid = stripePaid + inShopPaid;
  const price = Number(booking?.service?.price || 0);
  const balanceDue = Math.max(0, price - amountPaid);

  return createPortal(
    <div
      className="bdp-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bdp-title"
      data-testid="booking-detail-panel"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <aside className="bdp-panel" onClick={(e) => e.stopPropagation()}>
        <header className="bdp-header">
          <div style={{ minWidth: 0 }}>
            <h3 id="bdp-title" className="bdp-title" data-testid="bdp-client-name">
              {booking?.customer?.name || (loading ? 'Loading…' : 'Booking')}
            </h3>
            {booking && <StatusPill status={booking.status} refundStatus={booking.refund_status} />}
          </div>
          <button
            className="bdp-close"
            aria-label="Close"
            onClick={onClose}
            data-testid="bdp-close"
          ><X size={18} /></button>
        </header>

        {loading && (
          <div className="bdp-body" data-testid="bdp-loading">
            <p style={{ color: 'var(--mute)', fontSize: 13 }}>Loading booking…</p>
          </div>
        )}

        {!loading && !booking && (
          <div className="bdp-body">
            <p style={{ color: 'var(--mute)', fontSize: 13 }}>
              This booking couldn’t be loaded. It may have been removed.
            </p>
          </div>
        )}

        {!loading && booking && (
          <div className="bdp-body">
            {/* ----- Appointment ----- */}
            <Section title="Appointment">
              <Row label="Day">{fmtDate(booking.starts_at)}</Row>
              <Row label="Time">
                {fmtTime(booking.starts_at)} – {fmtTime(booking.ends_at)}
                <span className="bdp-muted"> · {durationMin(booking.starts_at, booking.ends_at)} min</span>
              </Row>
              <Row label="Service">{booking.service?.name || '—'}</Row>
              <Row label="Barber">{booking.barber?.name || '—'}</Row>
              <Row label="Price">{fmtMoney(price)}</Row>
            </Section>

            {/* ----- Payment ----- */}
            <Section title="Payment">
              <Row label="State">
                <span className={`bdp-paymentpill bdp-pay-${booking.payment_status || 'pending'}`}>
                  {(booking.payment_status || 'pending').replace('_', ' ')}
                </span>
                {booking.paid_in_full && <span className="bdp-tag">Paid in full</span>}
                {!booking.paid_in_full && booking.deposit_paid && <span className="bdp-tag">Deposit paid</span>}
              </Row>
              <Row label="Amount paid">{fmtMoney(amountPaid)}</Row>
              {balanceDue > 0 && (
                <>
                  <Row label="Balance due">{fmtMoney(balanceDue)}</Row>
                  <Row label="Payable in shop">
                    {IN_SHOP_PAYMENT_LABEL[inShopPayment] || IN_SHOP_PAYMENT_LABEL.either}
                  </Row>
                </>
              )}
              {inShopPaid > 0 && (
                <Row label="Balance paid">
                  {fmtMoney(inShopPaid)} · {booking.balance_paid_method === 'card' ? 'Card' : 'Cash'}
                </Row>
              )}
            </Section>

            {/* ----- Client — only fields present in the record ----- */}
            <Section title="Client">
              <Row label="Name">{booking.customer?.name || '—'}</Row>
              {booking.customer?.phone && (
                <Row label="Phone">
                  <a
                    href={`tel:${stripPhoneForTel(booking.customer.phone)}`}
                    className="bdp-taplink"
                    data-testid="bdp-phone-tel"
                  >
                    <Phone size={13} /> {formatPhoneForDisplay(booking.customer.phone)}
                  </a>
                </Row>
              )}
              {booking.customer?.email && (
                <Row label="Email">
                  <a
                    href={`mailto:${booking.customer.email}`}
                    className="bdp-taplink"
                    data-testid="bdp-email-link"
                  >
                    <Mail size={13} /> {booking.customer.email}
                  </a>
                </Row>
              )}
              <Row label="Marketing consent">
                {booking.customer?.marketing_consent
                  ? <span className="bdp-yes"><Check size={13} /> Yes</span>
                  : <span className="bdp-no"><XCircle size={13} /> No</span>}
              </Row>
            </Section>

            {/* ----- History ----- */}
            {history && (
              <Section title="History">
                <Row label="Total visits">{history.visits}</Row>
                <Row label="Last visit">
                  {history.lastVisit ? fmtDate(history.lastVisit) : 'First booking'}
                </Row>
                {booking.customer?.id && (
                  <button
                    type="button"
                    className="bdp-link"
                    onClick={() => {
                      onClose?.();
                      navigate(`/clients/${booking.customer.id}`);
                    }}
                    data-testid="bdp-open-client"
                  >Open client record →</button>
                )}
              </Section>
            )}

            {/* ----- Meta ----- */}
            <div className="bdp-meta">
              <div>Ref <span className="bdp-mono" data-testid="bdp-ref">{formatBookingRef({
                shopName: booking.shop?.name,
                startsAt: booking.starts_at,
                bookingNumber: booking.booking_number,
                uuid: booking.id,
              })}</span></div>
              <div>Booked <span className="bdp-mono">{fmtTimestamp(booking.created_at)}</span></div>
              <div>Source <span className="bdp-mono">{CHANNEL_LABEL[booking.channel] || booking.channel || 'Unknown'}</span></div>
            </div>

            {/* ----- Actions ----- */}
            <div className="bdp-actions" data-testid="bdp-actions">
              {actions.can.reschedule && (
                <button
                  type="button"
                  className="btn ghost"
                  disabled={actions.busy}
                  onClick={actions.openReschedule}
                  data-testid="bdp-reschedule"
                ><Repeat size={14} /> Reschedule</button>
              )}
              {actions.can.complete && (
                <button
                  type="button"
                  className="btn primary"
                  disabled={actions.busy}
                  onClick={actions.markCompleted}
                  data-testid="bdp-mark-completed"
                ><Check size={14} /> Mark completed</button>
              )}
              {actions.can.noShow && (
                <button
                  type="button"
                  className="btn ghost"
                  disabled={actions.busy}
                  onClick={actions.markNoShow}
                  data-testid="bdp-mark-noshow"
                ><Clock size={14} /> Mark no-show</button>
              )}
              {actions.can.cancel && (
                <button
                  type="button"
                  className="btn danger"
                  disabled={actions.busy}
                  onClick={actions.cancelBooking}
                  data-testid="bdp-cancel"
                ><XCircle size={14} /> Cancel booking</button>
              )}
              {actions.can.reopen && (
                <button
                  type="button"
                  className="btn ghost"
                  disabled={actions.busy}
                  onClick={actions.reopen}
                  data-testid="bdp-reopen"
                ><Repeat size={14} /> Reopen as confirmed</button>
              )}
            </div>
            {actions.confirmNode}
            {actions.rescheduleNode}
            {actions.paymentSheetNode}
          </div>
        )}
      </aside>
    </div>,
    document.body
  );
};

export default BookingDetailPanel;
